import { loadModules } from 'esri-loader';

const agolUrl = "https://odnr-hub.maps.arcgis.com";
const layerIdTripHistoryTemplate = 0;
const layerIdFavoritesTemplate = 1;
const templateServiceUrl = "https://services5.arcgis.com/ajRlmtxbNBjZggOT/arcgis/rest/services/DETOUR/FeatureServer";

/**
 * Register application ID and Portal URL
 * with the IdentityManager
 * @param appId
 * @param portalUrl
 * @returns Promise<void>
 */
export const initialize = async (appId, portalUrl) => {
  const [IdentityManager, OAuthInfo] = await loadModules([
    'esri/identity/IdentityManager',
    'esri/identity/OAuthInfo',
  ]);
  const oauthInfo = new OAuthInfo({
    appId,
    portalUrl,
    popup: true,
  });
  IdentityManager.registerOAuthInfos([oauthInfo]);
  return oauthInfo;
};

/**
 * Check current logged in status for current portal
 * @returns Promise<void>
 */
export const checkCurrentStatus = async (oauthInfo) => {
  const [IdentityManager] = await loadModules([
    'esri/identity/IdentityManager',
  ]);

  try {
    const credential = await IdentityManager.checkSignInStatus(
      `${oauthInfo.portalUrl}/sharing`
    );

    const user = await fetchUser(credential);

    return { credential, user };
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * Attempt to sign in,
 * first check current status
 * if not signed in, then go through
 * steps to get credentials
 * @returns Promise<`esri/identity/Credential`>
 */
export const signIn = async (oauthInfo) => {
  try {
    const { credential, user } = await checkCurrentStatus(oauthInfo);
    return { credential, user };
  } catch (error) {
    const { credential, user } = await fetchCredentials(oauthInfo);
    return { credential, user };
  }
};

/**
 * Sign the user out, but if we checked credentials
 * manually, make sure they are registered with
 * IdentityManager, so it can destroy them properly
 * @returns Promise<void>
 */
export const signOut = async (oauthInfo) => {
  const [IdentityManager] = await loadModules([
    'esri/identity/IdentityManager',
  ]);

  IdentityManager.destroyCredentials();
  window.location.reload();
};

/**
 * Get the credentials for the provided portal
 * @returns Promise<`esri/identity/Credential`>
 */
export const fetchCredentials = async (oauthInfo) => {
  const [IdentityManager] = await loadModules([
    'esri/identity/IdentityManager',
  ]);

  const credential = await IdentityManager.getCredential(
    `${oauthInfo.portalUrl}/sharing`,
    {
      error: null,
      oAuthPopupConfirmation: false,
      token: null,
    }
  );

  const user = await fetchUser(credential);

  return { credential, user };
};

export const fetchUser = async (credential) => {
  const [esriRequest] = await loadModules(['esri/request']);

  try {
    const response = await esriRequest(
      `https://www.arcgis.com/sharing/rest/community/users/${credential.userId}?f=json`
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};


/**
 * Load portal
 * @returns <Object>Portal
 */
export const loadPortal = async (tken) => {
  const [Portal] = await loadModules([
    "esri/portal/Portal",  
  ]);
   let portal = new Portal();
   // Setting authMode to immediate signs the user in once loaded
   portal.authMode = "immediate";
 
   // Once portal is loaded, user signed in
   await portal.load();
   return portal;
};

export const createDetourFolderIfNotPresent = async (portal) => {
  //Check if folder exists
  let folders = await portal.user.fetchFolders();

  if(!folders.length || !existsDetourFolder(folders)){
    //Create if not found
    await createDetourFolder(portal);
    folders = await portal.user.fetchFolders();
  }
  return getDetourFolder(folders);
};

export const createFeatureService = async (portal, detourFolderId, userToken) => {
  const url = agolUrl + "/sharing/rest/content/users/" + portal.user.username + "/" + detourFolderId + "/createService"

  const [esriRequest] = await loadModules(['esri/request']);

  const params = JSON.stringify({
    name: 'DETOUR_' + portal.user.username,
    hasStaticData: 'false',
    capabilities:'Query,Editing,Create,Update,Delete',
  });

  return esriRequest(url,{
    method: 'post',
    query: {
      createParameters: params,
      outputType: 'featureService',
      token: userToken,
      f:"json" // format
    },
   })
};

export const updateItem = async (portal, userToken, itemId) => {
  const url = agolUrl + "/sharing/rest/content/users/"  + portal.user.username + "/items/" + itemId + "/update"

  const [esriRequest] = await loadModules(['esri/request']);

  return esriRequest(url,{
    method: 'post',
    query: {
      tags: "detour,web app", 
      extent: "-128.971,28.626,-61.472,46.568", 
      f: "json", 
      token: userToken,
      thumbnail: "assets/images/agol_thumbnail.png",
    }
   })
};

export const getLayerTemplate = async (userToken) => {
  let url = templateServiceUrl + "/" + layerIdTripHistoryTemplate
  let paramsObj = {token: userToken, f: "json"}

  const [esriRequest] = await loadModules(['esri/request']);

  return esriRequest(url, {method: 'post', query: paramsObj})
};

export const getTableTemplate = async (userToken) => {
  let url = templateServiceUrl + "/" + layerIdFavoritesTemplate
  let paramsObj = {token: userToken, f: "json"}

  const [esriRequest] = await loadModules(['esri/request']);

  return esriRequest(url, {method: 'post', query: paramsObj})
};

export const addTemplatesToDef = async (serviceUrl, layerTemplate, tableTemplate, userToken) => {
  const params = {};
  let favoritesTable;
  let tripHistoryLayer;



  try{
    tripHistoryLayer = await getUserTripHistoryLayer(serviceUrl);
  }catch(e){
    console.log('Trip history layer not found in user\'s feature service');
  };
  if(tripHistoryLayer === undefined){
    params.layers = [layerTemplate];
  };
  try{
    favoritesTable = await getUserFavoritesTable(serviceUrl);
  }catch(e){
    console.log('Favorites table not found in user\'s feature service')
  };
  if(favoritesTable === undefined){
    params.tables = [tableTemplate];
  };
  


  if(params.hasOwnProperty('layers') || params.hasOwnProperty('tables')){
    let url = serviceUrl.replace("rest/services", "rest/admin/services") + "/addToDefinition"
    //Add table and layer templates to definition
  
    let paramsObj = {addToDefinition: JSON.stringify(params), token: userToken, f: "json"}
  
    const [esriRequest] = await loadModules(['esri/request']);
  
    return esriRequest(url, {method: 'post', query: paramsObj})
  }
};

/**
 * Gets user's favorites table
 * @returns Promise
 */
const getUserFavoritesTable = async (serviceUrl) => {
  let url = serviceUrl + "/" + layerIdFavoritesTemplate + "/query";
  let paramsObj = {
    where: '1=1',
    f:"json" // format
  }

  const [esriRequest] = await loadModules(['esri/request']);

  return esriRequest(url, {method: 'post', query: paramsObj});
};

/**
 * Gets user's trip history layer
 * @returns Promise
 */
const getUserTripHistoryLayer = async (serviceUrl) => {
  let url = serviceUrl + "/" + layerIdTripHistoryTemplate + "/query";
  let paramsObj = {
    where: '1=1',
    f:"json" // format
  }

  const [esriRequest] = await loadModules(['esri/request']);

  return esriRequest(url, {method: 'post', query: paramsObj});
};

/**
 * Checks for favorites table
 * @returns Boolean
 */
const existsTripHistory = (folders) => {
  return folders.some(folder => folder.title === 'DETOUR');
};

/**
 * Checks for DETOUR folder
 * @returns Boolean
 */
const existsDetourFolder = (folders) => {
  return folders.some(folder => folder.title === 'DETOUR');
};

/**
 * Checks for DETOUR folder
 * @returns Boolean
 */
export const existsDetourFeatureService = (items, username) => {
  return items.some(item => item.title === 'DETOUR_' + username);
};

/**
 * Get DETOUR folder
 * @returns Object
 */
const getDetourFolder = (folders) => {
  return folders.filter(folder => folder.title === 'DETOUR')[0];
};

/**
 * Get DETOUR feature service item
 * @returns Object
 */
export const getFeatureServiceItem = (items, username) => {
  return items.filter(item => item.title === 'DETOUR_' + username)[0];
};

/**
 * Creates DETOUR folder for portal user
 * @returns null
 */
export const createDetourFolder = async (portal) => {
  const [esriRequest] = await loadModules([
    'esri/request',
  ]);
  const folderName = 'DETOUR';
  return esriRequest(agolUrl + '/sharing/rest/content/users/' + portal.user.username + '/createFolder',{
    method: 'post',
    query: {
      title: folderName,
      f:"json"
    }, 
   }).then(  (r) => {
     console.log(r)
   }).catch( (e) => {
     console.log(e)
  });
};